import React from "react";
import {
  Box,
  IconButton,
  type SxProps,
  type Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * 依頼画面の見た目の部品。リアシスト（役所資料取得依頼 RealEstateDocumentFetchModal）と
 * 同じ値にそろえ、同じアプリの中で依頼画面の作りが違って見えないようにする。
 */

interface HelpTipProps {
  title: string;
  /** 読み上げで、どの項目の説明かを伝える */
  label: string;
}

/** 補足の説明を出すアイコン。SP ではタップしてすぐ出す。 */
export const HelpTip: React.FC<HelpTipProps> = ({ title, label }) => (
  <Tooltip title={title} placement="top" arrow enterTouchDelay={0}>
    <IconButton
      size="small"
      aria-label={`${label}の説明`}
      sx={{ color: "#64748b", p: 0.5 }}
    >
      <InfoOutlinedIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

interface SectionCardProps {
  title: string;
  /** 見出しの横のツールチップに出す説明 */
  titleTip?: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  /** 見出しを読み上げで区画名として使う（テストや支援技術から区画を探せるようにする） */
  ariaLabel?: string;
}

/** 区画ごとの薄いグレーのカード（リアシストの対象住所・対象地番のカードと同じ）。 */
export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  titleTip,
  description,
  icon,
  action,
  children,
  ariaLabel,
}) => (
  <Box
    component="section"
    aria-label={ariaLabel}
    sx={{
      p: { xs: 2, sm: 2.5 },
      backgroundColor: "#f8f9fa",
      borderRadius: "16px",
      border: "1px solid #e9ecef",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        mb: description ? 0.75 : 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography
          variant="subtitle2"
          component="h3"
          sx={{
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: 700,
            letterSpacing: "1px",
          }}
        >
          {title}
        </Typography>
        {titleTip ? <HelpTip title={titleTip} label={title} /> : null}
      </Box>
      {action}
    </Box>
    {description ? (
      <Typography
        variant="body2"
        sx={{ color: "#64748b", fontSize: "0.82rem", mb: 1.5 }}
      >
        {description}
      </Typography>
    ) : null}
    {children}
  </Box>
);

interface FieldLabelProps {
  children: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
}

/** 入力欄の上に置く項目名（リアシストの「対象住所（必須）」と同じ）。 */
export const FieldLabel: React.FC<FieldLabelProps> = ({
  children,
  required = false,
  htmlFor,
}) => (
  <Typography
    component={htmlFor ? "label" : "span"}
    htmlFor={htmlFor}
    variant="caption"
    color="text.secondary"
    sx={{
      fontSize: { xs: "11px", sm: "12px" },
      fontWeight: 600,
      letterSpacing: "0.5px",
      display: "block",
      mb: 0.75,
    }}
  >
    {children}
    {required ? "（必須）" : ""}
  </Typography>
);

/** 補足文（リアシストの補足文と同じ大きさ・色）。 */
export const HelpText: React.FC<{
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ children, sx }) => (
  <Typography
    variant="body2"
    sx={{ color: "#64748b", fontSize: "0.82rem", ...sx }}
  >
    {children}
  </Typography>
);

/** 白い入力欄（リアシストの表の中の入力欄と同じ）。 */
export const inputSx: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "white",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
      borderWidth: 2,
    },
  },
};

/** 選択肢を囲む枠（リアシストの書類選択のチェック欄と同じ）。選ばれていれば青くする。 */
export const optionBoxSx = (checked: boolean): SxProps<Theme> => ({
  m: 0,
  px: 2,
  py: 1.25,
  borderRadius: "12px",
  border: "2px solid",
  borderColor: checked ? "#3b82f6" : "#e2e8f0",
  backgroundColor: checked ? "#eff6ff" : "#ffffff",
  transition: "all 0.2s",
  "&:hover": { borderColor: checked ? "#3b82f6" : "#cbd5e1" },
  "& .MuiFormControlLabel-label": {
    fontSize: "0.9rem",
    fontWeight: checked ? 600 : 400,
    color: checked ? "#1e40af" : "inherit",
  },
});

interface StepIndicatorProps {
  step: number;
  labels: readonly string[];
}

/** 小さくまとまったステップ表示（リアシストと同じ。SP では番号だけを出す）。 */
export const StepIndicator: React.FC<StepIndicatorProps> = ({
  step,
  labels,
}) => (
  <Box
    role="progressbar"
    aria-valuenow={step}
    aria-valuemin={1}
    aria-valuemax={labels.length}
    aria-label="ステップ進行状況"
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      mb: 3,
    }}
  >
    {labels.map((label, index) => {
      const current = index + 1;
      return (
        <Box
          key={label}
          aria-current={step === current ? "step" : undefined}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: step >= current ? "#3b82f6" : "#e2e8f0",
              color: step >= current ? "#fff" : "#94a3b8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.8rem",
              fontWeight: 700,
            }}
          >
            {current}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: step === current ? "#3b82f6" : "#94a3b8",
              fontWeight: step === current ? 700 : 400,
              fontSize: "0.8rem",
              display: { xs: "none", sm: "block" },
            }}
          >
            {label}
          </Typography>
          {current < labels.length ? (
            <Box
              sx={{
                width: 32,
                height: 2,
                backgroundColor: step > current ? "#3b82f6" : "#e2e8f0",
              }}
            />
          ) : null}
        </Box>
      );
    })}
  </Box>
);

/** フッターのボタン（リアシストの「戻る」「次へ」と同じ大きさ）。 */
export const footerButtonSx: SxProps<Theme> = {
  minWidth: { xs: "100%", sm: "140px" },
  height: { xs: "48px", sm: "42px" },
  fontSize: { xs: "0.95rem", sm: "0.9rem" },
  fontWeight: 600,
  textTransform: "none",
};

/** フッターの戻る・キャンセル（リアシストと同じ枠線の色）。 */
export const footerSecondaryButtonSx: SxProps<Theme> = {
  ...footerButtonSx,
  borderRadius: "12px",
  border: "2px solid #e2e8f0",
  color: "#475569",
  "&:hover": { border: "2px solid #cbd5e1" },
};
